import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { supabase } from '../lib/supabaseClient';
import { getCurrentTenantSlug, RESERVED_SLUGS } from '../composables/useTenant';

// Tracks the tenant slug (if any) implied by the current URL and the org it
// resolves to. Initialized once at boot from main.js, before the auth store
// runs, so that login flows can gate on tenant membership.
//
// status states:
//   'none'        — root host (event-flow.co, dev.event-flow.co, localhost). No tenant gate.
//   'loading'     — slug present, RPC in flight
//   'found'       — slug present, org resolved
//   'not_found'   — slug present, no matching org (router sends to /workspace-not-found)
export const useTenantStore = defineStore('tenant', () => {
  const slug = ref(null);
  const org = ref(null);
  const status = ref('none');
  const error = ref(null);

  const isTenantGated = computed(() => status.value === 'found');

  // Memoized: the router guard and main.js both await this, and the first
  // caller wins. Re-running would never change the answer (the slug comes
  // from the hostname, which can't change without a full page load).
  let initPromise = null;
  function initialize() {
    if (!initPromise) initPromise = doInitialize();
    return initPromise;
  }

  async function doInitialize() {
    slug.value = getCurrentTenantSlug();

    if (!slug.value) {
      status.value = 'none';
      return;
    }

    // Reserved slugs can never belong to an org (DB check constraint blocks
    // them) — gate immediately without a lookup.
    if (RESERVED_SLUGS.has(slug.value)) {
      status.value = 'not_found';
      return;
    }

    status.value = 'loading';
    error.value = null;

    try {
      const { data, error: rpcError } = await supabase.rpc('get_public_org_by_subdomain', {
        slug: slug.value,
      });

      if (rpcError) throw rpcError;

      const row = Array.isArray(data) ? data[0] : data;
      if (row && row.id) {
        org.value = {
          id: row.id,
          name: row.name,
          brandPrimaryColor: row.brand_primary_color || null,
          brandAccentColor: row.brand_accent_color || null,
          logoUrl: row.logo_url || null,
        };
        status.value = 'found';
      } else {
        status.value = 'not_found';
      }
    } catch (err) {
      // Treat lookup failures as 'not_found' so we never silently fall through
      // to the public landing when a tenant URL was requested.
      console.warn('[tenant] Lookup failed for slug', slug.value, err);
      error.value = err.message || 'Lookup failed';
      status.value = 'not_found';
    }
  }

  return {
    slug,
    org,
    status,
    error,
    isTenantGated,
    initialize,
  };
});
